import * as React from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { cn } from "@/utils/shadcn-utils.js";
import { Button, buttonVariants } from "@/components/ui/button";
import { capitalize } from "@/utils/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  monthYearOnly?: boolean;
  yearOnly?: boolean; // NEW PROP
  placeholder?: string;
  monthYearViewOpenedByDefault?: boolean;
  minDate?: Date;
  maxDate?: Date;
  fullWidth?: boolean;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  monthYearOnly = false,
  yearOnly = false, // NEW PROP
  monthYearViewOpenedByDefault = false,
  placeholder = "Sélectionner le mois et l'année",
  minDate = new Date(1990, 0, 1),
  maxDate = new Date(2090, 11, 31),
  fullWidth = false,
  onMonthChange,
  ...props
}: CalendarProps) {
  const [isOpen, setIsOpen] = React.useState(monthYearViewOpenedByDefault ?? false);

  const [currentDate, setCurrentDate] = React.useState(() => {
    if (props.mode === "range" && props.selected) {
      const range = props.selected as { from?: Date; to?: Date };
      return range.from || new Date();
    } else if (props.selected && props.selected instanceof Date) {
      return props.selected;
    }
    return new Date();
  });

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(() => {
    if (props.mode !== "range" && props.selected instanceof Date) {
      return props.selected;
    }
    return undefined;
  });

  const [selectedRange, setSelectedRange] = React.useState<{ from?: Date; to?: Date }>(() => {
    if (props.mode === "range" && props.selected) {
      return props.selected as { from?: Date; to?: Date };
    }
    return {};
  });

  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLButtonElement>(null);

  const currentDateEnd = React.useMemo(() => {
    return new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1);
  }, [currentDate]);

  // NEW: Calculate the starting year for year grid display
  const yearGridStart = React.useMemo(() => {
    return Math.floor(currentDate.getFullYear() / 12) * 12;
  }, [currentDate]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isDateInBounds = (date: Date): boolean => {
    if (yearOnly) {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const minYear = new Date(minDate.getFullYear(), 0, 1);
      const maxYear = new Date(maxDate.getFullYear(), 0, 1);
      return firstDayOfYear >= minYear && firstDayOfYear <= maxYear;
    } else if (monthYearOnly) {
      const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
      return firstDayOfMonth >= minMonth && firstDayOfMonth <= maxMonth;
    } else {
      return date >= minDate && date <= maxDate;
    }
  };

  // NEW: Handle year selection
  const handleYearSelect = (year: number) => {
    const firstDayOfYear = new Date(year, 0, 1);
    if (!isDateInBounds(firstDayOfYear)) return;

    setSelectedDate(firstDayOfYear);
    if (props.onSelect && typeof props.onSelect === "function") {
      props.onSelect(firstDayOfYear, firstDayOfYear, {}, {} as any);
    }
    setIsOpen(false);
  };

  const handleMonthSelect = (date: Date) => {
    if (!isDateInBounds(date)) return;

    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

    if (props.mode === "range") {
      if (!selectedRange.from || (selectedRange.from && selectedRange.to)) {
        const newRange = { from: firstDayOfMonth, to: undefined };
        setSelectedRange(newRange);
        if (props.onSelect && typeof props.onSelect === "function") {
          props.onSelect(newRange, firstDayOfMonth, {}, {} as any);
        }
      } else if (selectedRange.from && !selectedRange.to) {
        const fromDate = selectedRange.from;
        const toDate = firstDayOfMonth;

        const finalRange = fromDate <= toDate ? { from: fromDate, to: toDate } : { from: toDate, to: fromDate };

        setSelectedRange(finalRange);
        if (props.onSelect && typeof props.onSelect === "function") {
          props.onSelect(finalRange, firstDayOfMonth, {}, {} as any);
        }
        setIsOpen(false);
      }
    } else {
      setSelectedDate(firstDayOfMonth);
      if (props.onSelect && typeof props.onSelect === "function") {
        props.onSelect(firstDayOfMonth, firstDayOfMonth, {}, {} as any);
      }
      setIsOpen(false);
    }
  };

  // NEW: Navigate year grid (by 12 years at a time)
  const navigateYearGrid = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        const newYear = prev.getFullYear() - 12;
        if (newYear >= minDate.getFullYear()) {
          newDate.setFullYear(newYear);
        }
      } else {
        const newYear = prev.getFullYear() + 12;
        if (newYear <= maxDate.getFullYear()) {
          newDate.setFullYear(newYear);
        }
      }
      return newDate;
    });
  };

  const navigateYear = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        const newYear = prev.getFullYear() - 1;
        if (monthYearOnly) {
          const testDate = new Date(newYear, prev.getMonth(), 1);
          if (testDate >= new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
            newDate.setFullYear(newYear);
          }
        } else {
          if (newYear >= minDate.getFullYear()) {
            newDate.setFullYear(newYear);
          }
        }
      } else {
        const newYear = prev.getFullYear() + 1;
        if (monthYearOnly) {
          const testDateRight = new Date(newYear + 1, prev.getMonth(), 1);
          if (testDateRight <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
            newDate.setFullYear(newYear);
          }
        } else {
          if (newYear <= maxDate.getFullYear()) {
            newDate.setFullYear(newYear);
          }
        }
      }
      return newDate;
    });
  };

  const formatRangeDisplay = () => {
    if (yearOnly) {
      return selectedDate ? format(selectedDate, "yyyy", { locale: fr }) : "Sélectionner l'année";
    } else if (props.mode === "range") {
      if (selectedRange.from && selectedRange.to) {
        return `${capitalize(format(selectedRange.from, "MMM yyyy", { locale: fr }))} - ${capitalize(format(selectedRange.to, "MMM yyyy", { locale: fr }))}`;
      } else if (selectedRange.from) {
        return capitalize(format(selectedRange.from, "MMMM yyyy", { locale: fr }));
      }
      return placeholder;
    }
    return selectedDate ? capitalize(format(selectedDate, "MMMM yyyy", { locale: fr })) : placeholder;
  };

  const isMonthInRange = (monthDate: Date) => {
    if (props.mode !== "range" || !selectedRange.from) return false;

    if (!selectedRange.to) {
      return monthDate.getTime() === selectedRange.from.getTime();
    }

    const monthTime = monthDate.getTime();
    const fromTime = selectedRange.from.getTime();
    const toTime = selectedRange.to.getTime();

    return monthTime >= fromTime && monthTime <= toTime;
  };

  const isMonthStart = (monthDate: Date) => {
    return props.mode === "range" && selectedRange.from && monthDate.getTime() === selectedRange.from.getTime();
  };

  const isMonthEnd = (monthDate: Date) => {
    return props.mode === "range" && selectedRange.to && monthDate.getTime() === selectedRange.to.getTime();
  };

  const canNavigatePrev = () => {
    if (yearOnly) {
      return yearGridStart - 12 >= minDate.getFullYear();
    } else if (monthYearOnly) {
      const testDate = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1);
      return testDate >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    } else {
      return currentDate.getFullYear() - 1 >= minDate.getFullYear();
    }
  };

  const canNavigateNext = () => {
    if (yearOnly) {
      return yearGridStart + 12 <= maxDate.getFullYear();
    } else if (monthYearOnly) {
      const testDateRight = new Date(currentDate.getFullYear() + 2, currentDate.getMonth(), 1);
      return testDateRight <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    } else {
      return currentDate.getFullYear() + 1 <= maxDate.getFullYear();
    }
  };

  const hasSelection = () => {
    if (props.mode === "range") {
      return selectedRange.from !== undefined;
    }
    return selectedDate !== undefined;
  };

  if (yearOnly) {
    return (
      <div className="relative">
        {!monthYearViewOpenedByDefault && (
          <Button
            variant="outline"
            size="sm"
            ref={inputRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className={hasSelection() ? "text-foreground" : "text-muted-foreground"}>
                {formatRangeDisplay()}
              </span>
            </div>
          </Button>
        )}

        {isOpen && (
          <div
            ref={dropdownRef}
            className={cn(
              "absolute top-full left-0 z-50 mt-1 rounded-md border bg-popover p-4 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
              "w-full min-w-[280px]"
            )}>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateYearGrid("prev")}
                disabled={!canNavigatePrev()}
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "h-7 w-7 p-0",
                  !canNavigatePrev() && "opacity-30 cursor-not-allowed"
                )}>
                <ChevronLeft className="h-3 w-3" />
              </button>

              <div className="text-sm font-medium">
                {yearGridStart} - {yearGridStart + 11}
              </div>

              <button
                onClick={() => navigateYearGrid("next")}
                disabled={!canNavigateNext()}
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "h-7 w-7 p-0",
                  !canNavigateNext() && "opacity-30 cursor-not-allowed"
                )}>
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 12 }, (_, i) => {
                const year = yearGridStart + i;
                const yearDate = new Date(year, 0, 1);
                const isSelected = selectedDate && selectedDate.getFullYear() === year;
                const isDisabled = !isDateInBounds(yearDate);

                return (
                  <button
                    key={i}
                    onClick={() => handleYearSelect(year)}
                    disabled={isDisabled}
                    className={cn(
                      "p-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors",
                      isSelected && "bg-blue-100 text-blue-550 font-semibold hover:bg-primary/80 hover:text-gray-100",
                      isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent hover:text-current"
                    )}>
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (monthYearOnly) {
    return (
      <div className="relative">
        {!monthYearViewOpenedByDefault && (
          <Button
            variant="outline"
            size="sm"
            ref={inputRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className={hasSelection() ? "text-foreground" : "text-muted-foreground"}>
                {formatRangeDisplay()}
              </span>
            </div>
          </Button>
        )}

        {isOpen && (
          <div
            ref={dropdownRef}
            className={cn(
              "absolute top-full left-0 z-50 mt-1 rounded-md border bg-popover p-4 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
              props.mode === "range" ? "w-full min-w-[580px]" : "w-full min-w-[280px]"
            )}>
            {props.mode === "range" ? (
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="text-xs font-medium text-muted-foreground mb-3 text-center">
                    {selectedRange.from && !selectedRange.to ? "Mois sélectionné" : "Mois de début"}
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => navigateYear("prev")}
                      disabled={!canNavigatePrev()}
                      className={cn(
                        buttonVariants({ variant: "outline", size: "sm" }),
                        "h-7 w-7 p-0",
                        !canNavigatePrev() && "opacity-30 cursor-not-allowed"
                      )}>
                      <ChevronLeft className="h-3 w-3" />
                    </button>

                    <div className="text-sm font-medium">{format(currentDate, "yyyy", { locale: fr })}</div>

                    <button
                      onClick={() => navigateYear("next")}
                      disabled={!canNavigateNext()}
                      className={cn(
                        buttonVariants({ variant: "outline", size: "sm" }),
                        "h-7 w-7 p-0",
                        !canNavigateNext() && "opacity-30 cursor-not-allowed"
                      )}>
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 12 }, (_, i) => {
                      const monthDate = new Date(currentDate.getFullYear(), i, 1);
                      const isInRange = isMonthInRange(monthDate);
                      const isStart = isMonthStart(monthDate);
                      const isEnd = isMonthEnd(monthDate);
                      const isDisabled = !isDateInBounds(monthDate);

                      return (
                        <button
                          key={i}
                          onClick={() => handleMonthSelect(monthDate)}
                          disabled={isDisabled}
                          className={cn(
                            "p-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors",
                            isInRange && !isStart && !isEnd && "bg-blue-100 text-blue-600",
                            (isStart || isEnd) && "bg-blue-500 text-white font-semibold",
                            isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent hover:text-current"
                          )}>
                          {format(monthDate, "MMM", { locale: fr })}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="w-px bg-border"></div>

                <div className="flex-1">
                  <div className="text-xs font-medium text-muted-foreground mb-3 text-center">Mois de fin</div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => navigateYear("prev")}
                      disabled={!canNavigatePrev()}
                      className={cn(
                        buttonVariants({ variant: "outline", size: "sm" }),
                        "h-7 w-7 p-0",
                        !canNavigatePrev() && "opacity-30 cursor-not-allowed"
                      )}>
                      <ChevronLeft className="h-3 w-3" />
                    </button>

                    <div className="text-sm font-medium">{format(currentDateEnd, "yyyy", { locale: fr })}</div>

                    <button
                      onClick={() => navigateYear("next")}
                      disabled={!canNavigateNext()}
                      className={cn(
                        buttonVariants({ variant: "outline", size: "sm" }),
                        "h-7 w-7 p-0",
                        !canNavigateNext() && "opacity-30 cursor-not-allowed"
                      )}>
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {Array.from({ length: 12 }, (_, i) => {
                      const monthDate = new Date(currentDateEnd.getFullYear(), i, 1);
                      const isInRange = isMonthInRange(monthDate);
                      const isStart = isMonthStart(monthDate);
                      const isEnd = isMonthEnd(monthDate);
                      const isDisabled = !isDateInBounds(monthDate);

                      return (
                        <button
                          key={i}
                          onClick={() => handleMonthSelect(monthDate)}
                          disabled={isDisabled}
                          className={cn(
                            "p-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors",
                            isInRange && !isStart && !isEnd && "bg-blue-100 text-blue-600",
                            (isStart || isEnd) && "bg-blue-500 text-white font-semibold",
                            isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent hover:text-current"
                          )}>
                          {format(monthDate, "MMM", { locale: fr })}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigateYear("prev")}
                    disabled={!canNavigatePrev()}
                    className={cn(
                      buttonVariants({ variant: "outline", size: "sm" }),
                      "h-7 w-7 p-0",
                      !canNavigatePrev() && "opacity-30 cursor-not-allowed"
                    )}>
                    <ChevronLeft className="h-3 w-3" />
                  </button>

                  <div className="text-sm font-medium">{format(currentDate, "yyyy", { locale: fr })}</div>

                  <button
                    onClick={() => navigateYear("next")}
                    disabled={!canNavigateNext()}
                    className={cn(
                      buttonVariants({ variant: "outline", size: "sm" }),
                      "h-7 w-7 p-0",
                      !canNavigateNext() && "opacity-30 cursor-not-allowed"
                    )}>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: 12 }, (_, i) => {
                    const monthDate = new Date(currentDate.getFullYear(), i, 1);
                    const isSelected =
                      selectedDate &&
                      selectedDate.getMonth() === i &&
                      selectedDate.getFullYear() === currentDate.getFullYear();
                    const isDisabled = !isDateInBounds(monthDate);

                    return (
                      <button
                        key={i}
                        onClick={() => handleMonthSelect(monthDate)}
                        disabled={isDisabled}
                        className={cn(
                          "p-2 text-sm rounded-md hover:bg-accent hover:text-accent-foreground transition-colors",
                          isSelected &&
                            "bg-blue-100 text-blue-550 font-semibold hover:bg-primary/80 hover:text-gray-100",
                          isDisabled && "opacity-30 cursor-not-allowed hover:bg-transparent hover:text-current"
                        )}>
                        {format(monthDate, "MMM", { locale: fr })}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <DayPicker
      locale={fr}
      showOutsideDays={showOutsideDays}
      fromDate={minDate}
      toDate={maxDate}
      disabled={[{ before: minDate }, { after: maxDate }]}
      className={cn("p-3", fullWidth && "w-full", className)}
      onMonthChange={onMonthChange}
      classNames={{
        months: `flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 ${fullWidth ? "w-full" : ""}`,
        month: `space-y-4 ${fullWidth ? "w-full" : ""}`,
        caption: `flex justify-center pt-1 relative items-center ${fullWidth ? "w-full" : ""}`,
        caption_label: "text-sm font-medium capitalize",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: `flex ${fullWidth ? "w-full" : ""}`,
        head_cell: fullWidth
          ? "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem]"
          : "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          `relative p-0 text-center text-sm focus-within:relative focus-within:z-20 ${fullWidth ? "flex-1" : ""}`,
          "[&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          fullWidth
            ? "h-8 w-full p-0 font-normal aria-selected:opacity-100"
            : "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected: `${fullWidth ? "bg-blue-100 text-blue-550 font-semibold" : "bg-[#F4F4F5] text-black"} hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground`,
        day_today: "bg-accent text-accent-foreground",
        day_outside: "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        // dropdown_month: "inline-flex items-center rounded-md px-3 py-2 text-sm font-medium",
        // dropdown_year: "inline-flex items-center rounded-md px-3 py-2 text-sm font-medium ml-2",
        // dropdown: "inline-flex items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        // vhidden: "sr-only",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => <ChevronLeft className={cn("h-4 w-4", className)} {...props} />,
        IconRight: ({ className, ...props }) => <ChevronRight className={cn("h-4 w-4", className)} {...props} />,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
